import { useState } from 'react';
import { useCRMContext } from '../CRMContext';
import { useLoadedData } from '../../../hooks/useLoadedData';
import { apiFetch } from '../../../api/core';
import { Organization } from '../types';

export interface ContactOrgLinkPayload {
  _id?: number;
  contact_id?: string;
  organization_id?: string;
  organization_name?: string;
  title?: string;
  is_active?: boolean;
}

export interface OrgOption {
  inputValue?: string;
  _id?: string;
  name: string;
}

export const useOrganizationLink = (
  value: Partial<ContactOrgLinkPayload>,
  setValue: (v: Partial<ContactOrgLinkPayload>) => void,
) => {
  const ctx = useCRMContext();

  // Search string for the Autocomplete
  const [searchValue, setSearchValue] = useState('');

  // Async load available organizations based on search
  const { data: availableOrganizations, isLoading } = useLoadedData<OrgOption[]>(
    `${ctx.apiPath}/organization?search=${searchValue}&limit=30`,
    [],
    !!searchValue
  );

  // Async creation of a new organization on the backend
  const createOrganization = async (nameToCreate: string) => {
    try {
      const res = await apiFetch<Organization>(`${ctx.apiPath}/organization`, "post", { name: nameToCreate });
      
      if (res._id) {
        // Update the provided payload with the real ID now that it's created
        setValue({
          ...(value || {}),
          organization_id: res._id,
          organization_name: res.name
        });
      }
    } catch (e) {
      console.error("Failed to create organization", e);
      // Fallback: keep the name, leave id undefined so it can be handled later if needed
    }
  };

  // Helper to handle Autocomplete changes
  const handleOrgChange = (newValue: string | OrgOption | null) => {
    if (typeof newValue === 'string') {
      // Free text typed and entered
      setValue({ ...value, organization_name: newValue, organization_id: undefined });
      createOrganization(newValue);
    } else if (newValue && newValue.inputValue) {
      // "Создать '...'" option clicked
      setValue({ ...value, organization_name: newValue.inputValue, organization_id: undefined });
      createOrganization(newValue.inputValue);
    } else {
      // Existing organization selected
      setValue({ ...value, organization_id: newValue?._id, organization_name: newValue?.name });
    }
  };

  const handleFieldChange = (updates: Partial<ContactOrgLinkPayload>) => {
    setValue({ ...value, ...updates });
  };

  return {
    // value,
    searchValue,
    setSearchValue,
    availableOrganizations,
    isLoading,
    handleOrgChange,
    handleFieldChange
  };
};